import React, { useCallback, useEffect, useState } from "react";
import {
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import {
  Briefcase,
  Bookmark,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Landmark,
  Loader2,
  TrendingUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WatchlistService } from "@/features/watchlists/services/watchlist-service";
import type { Watchlist } from "@/schemas";
import {
  CalendarService,
  type CalendarAssetMeta,
  type CalendarSource,
} from "@/services/calendar-service";

import { UnifiedCalendar, type CalendarEvent } from "./UnifiedCalendar";

type CalendarTab = "global" | "earnings" | "dividends";

function getCalendarRequestSource(source: CalendarSource): {
  source: CalendarSource;
  watchlistId?: string;
} {
  if (source === "my-items" || source === "synced" || source === "portfolio") {
    return { source };
  }

  return {
    source: "watchlist",
    watchlistId: source,
  };
}

function buildAssetMap(assets: CalendarAssetMeta[]): Record<string, CalendarAssetMeta> {
  return assets.reduce<Record<string, CalendarAssetMeta>>((acc, asset) => {
    acc[asset.asset_id] = asset;
    return acc;
  }, {});
}

export default function CalendarPage() {
  const [viewDate, setViewDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<"month" | "week">("month");
  const [activeTab, setActiveTab] = useState<CalendarTab>("global");
  const [source, setSource] = useState<CalendarSource>("my-items");
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadWatchlists = async () => {
      try {
        const wlData = await WatchlistService.list();
        setWatchlists(wlData);
      } catch (error) {
        console.error("Failed to load watchlists", error);
      }
    };

    void loadWatchlists();
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);

    try {
      const start = format(
        viewMode === "month"
          ? startOfMonth(viewDate)
          : startOfWeek(viewDate, { weekStartsOn: 1 }),
        "yyyy-MM-dd",
      );
      const end = format(
        viewMode === "month"
          ? endOfMonth(viewDate)
          : endOfWeek(viewDate, { weekStartsOn: 1 }),
        "yyyy-MM-dd",
      );

      const requestSource = getCalendarRequestSource(source);

      const buildDividendEvents = (
        divCalendars: Awaited<ReturnType<typeof CalendarService.getDividends>>["dividends"],
        assetMap: Record<string, CalendarAssetMeta>,
      ) => {
        const divEvents: CalendarEvent[] = [];

        divCalendars.forEach((cal) => {
          const assetSymbol = assetMap[cal.asset_id]?.symbol || "Asset";
          const assetName = assetMap[cal.asset_id]?.name;

          if (cal.ex_dividend_date) {
            const exDivDate = new Date(cal.ex_dividend_date);
            if (exDivDate >= new Date(start) && exDivDate <= new Date(end)) {
              divEvents.push({
                id: `div-ex-${cal.asset_id}-${cal.ex_dividend_date}`,
                date: exDivDate,
                title: "Ex-dividend",
                subTitle: "Ex-dividend",
                type: "dividend",
                assetSymbol,
                assetName,
                assetType: assetMap[cal.asset_id]?.type,
                assetWebsite: assetMap[cal.asset_id]?.website,
                amount: cal.forward_dividend_rate ? cal.forward_dividend_rate / 4 : undefined,
                currency: "USD",
              });
            }
          }

          if (cal.dividend_date) {
            const divDate = new Date(cal.dividend_date);
            if (divDate >= new Date(start) && divDate <= new Date(end)) {
              divEvents.push({
                id: `div-pay-${cal.asset_id}-${cal.dividend_date}`,
                date: divDate,
                title: "Dividend Payment",
                subTitle: "Payment",
                type: "dividend",
                assetSymbol,
                assetName,
                assetType: assetMap[cal.asset_id]?.type,
                assetWebsite: assetMap[cal.asset_id]?.website,
                amount: cal.forward_dividend_rate ? cal.forward_dividend_rate / 4 : undefined,
                currency: "USD",
              });
            }
          }
        });

        const seenDivKeys = new Set<string>();
        return divEvents.filter((ev) => {
          const key = `${ev.assetSymbol}-${format(ev.date, "yyyy-MM-dd")}-${ev.subTitle}`;
          if (seenDivKeys.has(key)) return false;
          seenDivKeys.add(key);
          return true;
        });
      };

      const buildEarningsEvents = (
        earnData: Awaited<ReturnType<typeof CalendarService.getEarnings>>["earnings"],
        assetMap: Record<string, CalendarAssetMeta>,
      ) => {
        const allEarnEvents: CalendarEvent[] = [];

        earnData.forEach((cal) => {
          if (!cal.earnings_dates || !Array.isArray(cal.earnings_dates)) {
            return;
          }

          cal.earnings_dates.forEach((ev) => {
            if (!ev.date) {
              return;
            }

            const evDate = new Date(ev.date);
            if (isNaN(evDate.getTime())) {
              return;
            }

            if (evDate >= new Date(start) && evDate <= new Date(end)) {
              allEarnEvents.push({
                id: `earn-${cal.asset_id}-${ev.date}`,
                date: evDate,
                title: "Earnings",
                type: "earnings",
                assetSymbol: assetMap[cal.asset_id]?.symbol || "Asset",
                assetName: assetMap[cal.asset_id]?.name,
                assetType: assetMap[cal.asset_id]?.type,
                assetWebsite: assetMap[cal.asset_id]?.website,
                estimate: ev.eps_estimate,
                actual: ev.eps_actual,
              });
            }
          });
        });

        const seenEarnKeys = new Set<string>();
        return allEarnEvents.filter((ev) => {
          const key = `${ev.assetSymbol}-${format(ev.date, "yyyy-MM-dd")}`;
          if (seenEarnKeys.has(key)) return false;
          seenEarnKeys.add(key);
          return true;
        });
      };

      if (activeTab === "dividends") {
        const result = await CalendarService.getDividends({
          ...requestSource,
          startDate: start,
          endDate: end,
        });
        setEvents(buildDividendEvents(result.dividends, buildAssetMap(result.assets)));
      } else if (activeTab === "earnings") {
        const result = await CalendarService.getEarnings({
          ...requestSource,
          startDate: start,
          endDate: end,
        });
        setEvents(buildEarningsEvents(result.earnings, buildAssetMap(result.assets)));
      } else {
        const [dividendResult, earningsResult] = await Promise.all([
          CalendarService.getDividends({
            ...requestSource,
            startDate: start,
            endDate: end,
          }),
          CalendarService.getEarnings({
            ...requestSource,
            startDate: start,
            endDate: end,
          }),
        ]);

        const dividendAssetMap = buildAssetMap(dividendResult.assets);
        const earningsAssetMap = buildAssetMap(earningsResult.assets);

        setEvents(
          [
            ...buildDividendEvents(dividendResult.dividends, dividendAssetMap),
            ...buildEarningsEvents(earningsResult.earnings, earningsAssetMap),
          ].sort((a, b) => a.date.getTime() - b.date.getTime()),
        );
      }
    } catch (error) {
      console.error("Failed to fetch events", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, source, viewDate, viewMode]);

  useEffect(() => {
    void fetchEvents();
  }, [fetchEvents]);

  const handlePrev = () => {
    setViewDate((prev) => (viewMode === "month" ? subMonths(prev, 1) : subWeeks(prev, 1)));
  };

  const handleNext = () => {
    setViewDate((prev) => (viewMode === "month" ? addMonths(prev, 1) : addWeeks(prev, 1)));
  };

  const handleToday = () => {
    setViewDate(new Date());
  };

  return (
    <div className="flex flex-col gap-6 h-[calc(100vh-120px)]">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <p className="text-xl font-bold">{format(viewDate, "MMMM yyyy")}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-muted rounded-lg p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handlePrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 px-3 text-xs font-bold" onClick={handleToday}>
              Today
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <Select value={viewMode} onValueChange={(v: "month" | "week") => setViewMode(v)}>
            <SelectTrigger className="w-[110px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="week">Week</SelectItem>
            </SelectContent>
          </Select>

          <Select value={source} onValueChange={(value) => setSource(value as CalendarSource)}>
            <SelectTrigger className="w-[210px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="synced">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-3.5 w-3.5" />
                  <span>ALL synced assets</span>
                </div>
              </SelectItem>
              <SelectItem value="my-items">
                <div className="flex items-center gap-2">
                  <Bookmark className="h-3.5 w-3.5 fill-muted-foreground" />
                  <span>My items</span>
                </div>
              </SelectItem>
              <SelectItem value="portfolio">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-3.5 w-3.5" />
                  <span>All portfolios</span>
                </div>
              </SelectItem>
              {watchlists.map((wl) => (
                <SelectItem key={wl.watchlist_id} value={wl.watchlist_id}>
                  <div className="flex items-center gap-2">
                    <Bookmark className="h-3.5 w-3.5" />
                    <span>{wl.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} className="flex-1 flex flex-col min-h-0" onValueChange={(v) => setActiveTab(v as CalendarTab)}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
          <TabsList variant="line" className="sm:w-auto">
            <TabsTrigger value="global" className="gap-2">
              <CalendarIcon className="h-4 w-4" /> Global
            </TabsTrigger>
            <TabsTrigger value="earnings" className="gap-2">
              <TrendingUp className="h-4 w-4" /> Earnings
            </TabsTrigger>
            <TabsTrigger value="dividends" className="gap-2">
              <Landmark className="h-4 w-4" /> Dividends
            </TabsTrigger>
          </TabsList>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 lg:justify-end">
            {activeTab === "global" && (
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-sky-400" />
                  Earnings
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-amber-400" />
                  Ex-dividend
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                  Payment
                </span>
              </div>
            )}

            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground animate-in fade-in">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Updating events...</span>
              </div>
            )}
          </div>
        </div>

        <TabsContent value="global" className="flex-1 min-h-0 mt-0 focus-visible:ring-0">
          <UnifiedCalendar viewDate={viewDate} viewMode={viewMode} events={events} />
        </TabsContent>
        <TabsContent value="earnings" className="flex-1 min-h-0 mt-0 focus-visible:ring-0">
          <UnifiedCalendar viewDate={viewDate} viewMode={viewMode} events={events} />
        </TabsContent>
        <TabsContent value="dividends" className="flex-1 min-h-0 mt-0 focus-visible:ring-0">
          <UnifiedCalendar viewDate={viewDate} viewMode={viewMode} events={events} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
